/** Числа для показа — ВСЕГДА в русской локали.
 *
 *  Правило Максима 19.08.2026: десятичный разделитель на портале один — ЗАПЯТАЯ.
 *  Точка приходила оттуда, где число собирали через `toFixed`: он не знает про
 *  локаль и всегда ставит точку, поэтому в одной карточке рядом оказывались
 *  «32,6 %» и «33.5 %».
 *
 *  ⚠️ `toFixed` для ВЫВОДА не использовать. Для вычислений — сколько угодно.
 */

/** Дробное число: «12,4». digits — знаков после запятой. */
export const dec = (v: number, digits = 1) =>
  v.toLocaleString("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** Процент: «12,4 %». null → «—». */
export const pct = (v: number | null | undefined, digits = 1) =>
  v == null ? "—" : `${dec(v, digits)}%`;

/** Целое с разрядами: «1 234». */
export const int = (v: number) => Math.round(v).toLocaleString("ru-RU");

/** Короткий формат: «1,2М», «15К», «834». Хвостовой ноль убирается («1,0М» → «1М»).
 *
 *  ⚠️ Раньше это была одна и та же функция в шести файлах, и каждая делала
 *  `.toFixed(1).replace(/\.0$/, "")` — то есть точка и отсечение по точке. При
 *  переводе на запятую такая регулярка молча перестаёт работать и даёт «1,0М».
 */
export const compact = (n: number | null | undefined): string => {
  if (n == null) return "—";
  const a = Math.abs(n);
  const cut = (s: string) => s.replace(/,0$/, "");
  if (a >= 1_000_000) return cut(dec(n / 1_000_000, a >= 10_000_000 ? 0 : 1)) + "М";
  if (a >= 1_000) return cut(dec(n / 1_000, a >= 10_000 ? 0 : 1)) + "К";
  return int(n);
};

/** Подпись оси графика: «0,25», «1 234». Recharts по умолчанию печатает число
 *  как есть, то есть с точкой — на дробных шкалах это единственный источник
 *  точек, который поиском по коду не находится (нашли браузером 19.08.2026). */
export const tick = (v: number | string) =>
  Number(v).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
